import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';
import {GalleryRegistrationStatus, GalleryRegistrationDetails} from '../components/gallery/GalleryRegistrationDetails';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  DollarSign, 
  Users, 
  Calendar,
  Gift,
  Palette,
  ChevronDown,
  ChevronUp,
  Eye,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Settings,
  Bell,
  Clock,
  Mail,
  UserPlus,
  X,
  Save,
  Trash2,
  Send,
  Ghost
} from 'lucide-react';

const API_URL = "";

export const AdminRegistrations = () => {
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [galleryFees, setGalleryFees] = useState([]);
  const [galleryFeeError, setGalleryFeeError] = useState('');
  const [workshopSettings, setWorkshopSettings] = useState({});
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeTab, setActiveTab] = useState('registrations');
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [editSlots, setEditSlots] = useState(12);
  const [editNotifyAt, setEditNotifyAt] = useState(3);
  const [orphanedRecords, setOrphanedRecords] = useState([]);
  const [orphanedExpanded, setOrphanedExpanded] = useState(false);
  const navigate = useNavigate();

  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);

  useEffect(() => {
    if (isAuthenticated && isAdmin && token) {
      fetchData();
    }
  }, [isAuthenticated, isAdmin, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch registrations
      const regResponse = await fetch(`${API_URL}/api/registrations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (regResponse.ok) {
        setRegistrations(await regResponse.json());
      }

      try {
        const feesResponse=await fetch(`${API_URL}/api/registrations/by-type/gallery_application?limit=500`, {headers:{'Authorization':`Bearer ${token}`}});
        if(!feesResponse.ok)throw new Error('Gallery entry fees could not load. Use Refresh to try again.');
        setGalleryFees(await feesResponse.json());setGalleryFeeError('');
      }catch(error){setGalleryFeeError(error.message||'Gallery entry fees could not load. Use Refresh to try again.');}

      // Fetch workshop settings
      const settingsResponse = await fetch(`${API_URL}/api/workshops/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        const settingsMap = {};
        settings.forEach(s => { settingsMap[s.workshop_id] = s; });
        setWorkshopSettings(settingsMap);
      }

      // Fetch waitlist
      const waitlistResponse = await fetch(`${API_URL}/api/waitlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (waitlistResponse.ok) {
        setWaitlist(await waitlistResponse.json());
      }

      // Fetch orphaned registration records (read-only diagnostic)
      try {
        const orphanedResponse = await fetch(`${API_URL}/api/admin/registrations/orphaned`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (orphanedResponse.ok) {
          setOrphanedRecords(await orphanedResponse.json());
        }
      } catch (e) {
        // Non-blocking — orphan check is purely informational
        console.warn('Orphaned records fetch failed:', e);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get workshop name from registration
  const getWorkshopName = (reg) => {
    if (reg.registration_data?.workshop_name) return reg.registration_data.workshop_name;
    if (reg.registration_data?.program_name) return reg.registration_data.program_name;
    if (reg.items && reg.items.length > 0) return reg.items[0].name || 'Unknown';
    return 'Unknown';
  };

  // Format registration_type for human-readable badge display
  const formatRegType = (type) => {
    const map = {
      'open_session': 'Open Session',
      'workshop': 'Workshop',
      'membership': 'Membership',
      'class': 'Class',
      'gallery_application': 'Gallery entry fee',
    };
    return map[type] || (type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Unknown');
  };

  const getWorkshopId = (reg) => {
    return reg.registration_data?.workshop_id || reg.registration_data?.program_id || null;
  };

  // Get max slots for a workshop (from settings or default 12)
  const getMaxSlots = (workshopId) => {
    return workshopSettings[workshopId]?.max_slots || 12;
  };

  const getNotifyAt = (workshopId) => {
    return workshopSettings[workshopId]?.notify_at_slots_remaining || 3;
  };

  // Calculate workshop slot counts
  const getWorkshopSlotCounts = () => {
    const workshopRegs = registrations.filter(r => 
      r.registration_type === 'workshop' || r.registration_type === 'open_session'
    );
    
    const counts = {};
    workshopRegs.forEach(reg => {
      const workshopName = getWorkshopName(reg);
      const workshopId = getWorkshopId(reg) || workshopName;
      
      if (!counts[workshopId]) {
        counts[workshopId] = {
          name: workshopName,
          id: workshopId,
          registrations: [],
          count: 0,
          maxSlots: getMaxSlots(workshopId),
          notifyAt: getNotifyAt(workshopId)
        };
      }
      counts[workshopId].registrations.push(reg);
      counts[workshopId].count += 1;
    });
    
    return Object.values(counts).sort((a, b) => b.count - a.count);
  };

  // Get waitlist count for a workshop
  const getWaitlistCount = (workshopId) => {
    return waitlist.filter(w => w.workshop_id === workshopId && w.status === 'waiting').length;
  };

  const workshopSlots = getWorkshopSlotCounts();

  // Save workshop settings
  const handleSaveWorkshopSettings = async (workshopId, workshopName) => {
    try {
      const response = await fetch(`${API_URL}/api/workshops/settings/${workshopId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          max_slots: editSlots,
          notify_at_slots_remaining: editNotifyAt
        })
      });

      if (response.ok) {
        toast.success('Workshop settings saved');
        setEditingWorkshop(null);
        // Update local state
        setWorkshopSettings({
          ...workshopSettings,
          [workshopId]: {
            ...workshopSettings[workshopId],
            workshop_id: workshopId,
            workshop_name: workshopName,
            max_slots: editSlots,
            notify_at_slots_remaining: editNotifyAt
          }
        });
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  // Notify waitlist member
  const handleNotifyWaitlist = async (entryId) => {
    try {
      const response = await fetch(`${API_URL}/api/waitlist/${entryId}/notify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Notification sent');
        fetchData();
      } else {
        toast.error('Failed to send notification');
      }
    } catch (error) {
      toast.error('Error sending notification');
    }
  };

  // Remove from waitlist
  const handleRemoveFromWaitlist = async (entryId) => {
    if (!window.confirm('Remove this person from the waitlist?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/waitlist/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Removed from waitlist');
        setWaitlist(waitlist.filter(w => w.id !== entryId));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove');
      }
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      toast.error('Error removing from waitlist');
    }
  };

  // Send test email
  const handleTestEmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Error sending test email');
    }
  };

  // Resend confirmation email
  const handleResendConfirmation = async (registrationId) => {
    try {
      const response = await fetch(`${API_URL}/api/registrations/${registrationId}/resend-confirmation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Confirmation email sent to ${data.sent_to}`);
        fetchData(); // Refresh to update email status
      } else {
        toast.error(data.detail || 'Failed to resend confirmation');
      }
    } catch (error) {
      toast.error('Error resending confirmation');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'workshop': return <Palette className="text-purple-600" size={18} />;
      case 'membership': return <Users className="text-blue-600" size={18} />;
      case 'donation': return <Gift className="text-pink-600" size={18} />;
      case 'open_session': return <Calendar className="text-green-600" size={18} />;
      default: return <DollarSign className="text-stone-600" size={18} />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'workshop': return 'bg-purple-100 text-purple-800';
      case 'membership': return 'bg-blue-100 text-blue-800';
      case 'donation': return 'bg-pink-100 text-pink-800';
      case 'open_session': return 'bg-green-100 text-green-800';
      default: return 'bg-stone-100 text-stone-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'pending': case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'failed': case 'expired': return 'bg-red-100 text-red-800';
      case 'notified': return 'bg-blue-100 text-blue-800';
      default: return 'bg-stone-100 text-stone-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Filter and sort registrations
  const displayedRegistrations=Array.from(new Map([...registrations,...galleryFees].map(record=>[record.payment_id||record.id,record])).values());
  const filteredRegistrations = displayedRegistrations
    .filter(reg => {
      const workshopName = getWorkshopName(reg).toLowerCase();
      const matchesSearch = 
        reg.payer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.payer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        workshopName.includes(searchTerm.toLowerCase());
      
      let matchesType = true;
      if (filterType !== 'all') matchesType = reg.registration_type === filterType;
      
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'created_at') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else if (sortField === 'total_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

  // Stats
  const stats = {
    total: displayedRegistrations.length,
    totalRevenue: displayedRegistrations.reduce((sum, r) => sum + (r.total_amount || 0), 0),
    byType: {
      workshop: registrations.filter(r => r.registration_type === 'workshop').length,
      membership: registrations.filter(r => r.registration_type === 'membership').length,
      donation: registrations.filter(r => r.registration_type === 'donation').length,
      open_session: registrations.filter(r => r.registration_type === 'open_session').length,
    },
    waitlistTotal: waitlist.filter(w => w.status === 'waiting').length
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Workshop/Item', 'Name', 'Email', 'Amount', 'Status', 'Payment ID'];
    const rows = filteredRegistrations.map(reg => [
      formatDate(reg.created_at),
      reg.registration_type,
      getWorkshopName(reg),
      reg.payer_name,
      reg.payer_email,
      reg.total_amount,
      reg.status,
      reg.payment_id
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(c => `"${c || ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registrations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  if (authLoading || loading) {
    return null; // AdminLayout handles loading
  }

  if (!isAdmin) return null;

  return (
    <AdminLayout title="Registrations Dashboard" subtitle="Registrations, Workshops & Waitlist Management">
      <div data-testid="admin-registrations-page">
      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'registrations' ? 'default' : 'outline'}
            onClick={() => setActiveTab('registrations')}
            className={activeTab === 'registrations' ? 'bg-cafa-red' : ''}
          >
            <DollarSign size={16} className="mr-2" />
            Registrations ({stats.total})
          </Button>
          <Button
            variant={activeTab === 'workshops' ? 'default' : 'outline'}
            onClick={() => setActiveTab('workshops')}
            className={activeTab === 'workshops' ? 'bg-cafa-red' : ''}
          >
            <Palette size={16} className="mr-2" />
            Workshops ({workshopSlots.length})
          </Button>
          <Button
            variant={activeTab === 'waitlist' ? 'default' : 'outline'}
            onClick={() => setActiveTab('waitlist')}
            className={activeTab === 'waitlist' ? 'bg-cafa-red' : ''}
          >
            <Clock size={16} className="mr-2" />
            Waitlist ({stats.waitlistTotal})
          </Button>
          <Button
            variant="outline"
            onClick={handleTestEmail}
            className="ml-auto"
            title="Send test email"
          >
            <Mail size={16} className="mr-2" />
            Test Email
          </Button>
        </div>

        {/* Orphaned Records Read-Only Warning Panel */}
        {orphanedRecords.length > 0 && (
          <Card className="mb-6 border-amber-300 bg-amber-50/60" data-testid="orphaned-records-panel">
            <CardHeader className="pb-2">
              <button
                type="button"
                onClick={() => setOrphanedExpanded(v => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
                data-testid="orphaned-toggle-btn"
                aria-expanded={orphanedExpanded}
                aria-controls="orphaned-records-content"
                aria-label={orphanedExpanded ? 'Hide orphaned registration records' : 'Show orphaned registration records'}
              >
                <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                  <Ghost size={18} className="text-amber-700" />
                  Orphaned Registration Records ({orphanedRecords.length})
                  <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold bg-amber-200 text-amber-900 rounded uppercase tracking-wide">
                    Read-only
                  </span>
                </CardTitle>
                {orphanedExpanded ? <ChevronUp size={18} className="text-amber-700" /> : <ChevronDown size={18} className="text-amber-700" />}
              </button>
              <p className="text-xs text-amber-800 mt-1">
                These registrations point to workshops or programs that no longer exist in active management. Nothing has been deleted — this is a diagnostic view only. Investigate before taking any action.
              </p>
            </CardHeader>
            {orphanedExpanded && (
              <CardContent className="pt-2" id="orphaned-records-content">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-100 text-amber-900">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-3 py-2 text-left font-medium">Name / ID</th>
                        <th className="px-3 py-2 text-right font-medium">Registrations</th>
                        <th className="px-3 py-2 text-right font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-200">
                      {orphanedRecords.map((rec, i) => (
                        <tr key={`${rec.type}_${rec.id}_${i}`} className="hover:bg-amber-100/40">
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 text-xs rounded bg-white border border-amber-300 text-amber-800">
                              {rec.type}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-stone-800">
                            <div className="font-medium">{rec.name}</div>
                            <div className="text-xs text-stone-500">ID: {rec.id}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-amber-900">{rec.reg_count}</td>
                          <td className="px-3 py-2 text-right text-emerald-700">{formatCurrency(rec.revenue || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-100/60 border border-amber-200 rounded p-2">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Common causes: a workshop was deleted from the schedule, a program ID changed, or a legacy import. To restore visibility, recreate the workshop/program with the same ID, or migrate these registrations manually. <strong>Do not delete</strong> these records without confirming refund/communication status with the registrants.
                  </span>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase">Total</p>
              <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase">Revenue</p>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.totalRevenue)}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase">Workshops</p>
              <p className="text-2xl font-bold text-purple-600">{stats.byType.workshop}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase">Memberships</p>
              <p className="text-2xl font-bold text-blue-600">{stats.byType.membership}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase">Donations</p>
              <p className="text-2xl font-bold text-pink-600">{stats.byType.donation}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 uppercase">Waitlist</p>
              <p className="text-2xl font-bold text-orange-600">{stats.waitlistTotal}</p>
            </CardContent>
          </Card>
        </div>

        {/* WORKSHOPS TAB */}
        {activeTab === 'workshops' && (
          <Card className="border-stone-200">
            <CardHeader className="border-b border-stone-200">
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <Palette className="text-purple-600" size={20} />
                Workshop Capacity & Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {workshopSlots.length === 0 ? (
                <p className="text-center text-stone-500 py-8">No workshop registrations yet</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {workshopSlots.map((workshop) => {
                    const maxSlots = workshop.maxSlots;
                    const percentage = (workshop.count / maxSlots) * 100;
                    const isFull = workshop.count >= maxSlots;
                    const isNearFull = workshop.count >= maxSlots - workshop.notifyAt;
                    const isEditing = editingWorkshop === workshop.id;
                    const waitlistCount = getWaitlistCount(workshop.id);
                    
                    return (
                      <div 
                        key={workshop.id} 
                        className={`p-4 rounded-lg border ${
                          isFull ? 'bg-red-50 border-red-200' : 
                          isNearFull ? 'bg-cafa-red-50 border-cafa-blue-200' : 
                          'bg-white border-stone-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-medium text-stone-900 text-sm leading-tight flex-1 pr-2">
                            {workshop.name}
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (isEditing) {
                                setEditingWorkshop(null);
                              } else {
                                setEditingWorkshop(workshop.id);
                                setEditSlots(maxSlots);
                                setEditNotifyAt(workshop.notifyAt);
                              }
                            }}
                          >
                            {isEditing ? <X size={16} /> : <Settings size={16} />}
                          </Button>
                        </div>
                        
                        {isEditing ? (
                          <div className="space-y-3 p-3 bg-white rounded border border-stone-200">
                            <div>
                              <Label className="text-xs">Max Slots</Label>
                              <Input
                                type="number"
                                value={editSlots}
                                onChange={(e) => setEditSlots(parseInt(e.target.value) || 12)}
                                min={1}
                                max={100}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Notify Admin When Slots Remaining ≤</Label>
                              <Input
                                type="number"
                                value={editNotifyAt}
                                onChange={(e) => setEditNotifyAt(parseInt(e.target.value) || 3)}
                                min={1}
                                max={20}
                                className="mt-1"
                              />
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => handleSaveWorkshopSettings(workshop.id, workshop.name)}
                              className="w-full bg-cafa-red hover:bg-cafa-red-900"
                            >
                              <Save size={14} className="mr-1" /> Save Settings
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-2xl font-bold ${
                                isFull ? 'text-red-600' : isNearFull ? 'text-cafa-red-600' : 'text-emerald-600'
                              }`}>
                                {workshop.count}/{maxSlots}
                              </span>
                              {isFull ? (
                                <span className="flex items-center gap-1 text-red-600 text-sm font-medium">
                                  <AlertCircle size={16} /> FULL
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-emerald-600 text-sm">
                                  <CheckCircle size={16} /> {maxSlots - workshop.count} left
                                </span>
                              )}
                            </div>
                            
                            {/* Progress bar */}
                            <div className="w-full bg-stone-200 rounded-full h-3 mb-3">
                              <div 
                                className={`h-3 rounded-full transition-all ${
                                  isFull ? 'bg-red-500' : isNearFull ? 'bg-cafa-red-500' : 'bg-emerald-500'
                                }`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                              />
                            </div>
                            
                            {/* Waitlist indicator */}
                            {waitlistCount > 0 && (
                              <div className="flex items-center gap-2 text-orange-600 text-sm mb-2">
                                <Clock size={14} />
                                <span>{waitlistCount} on waitlist</span>
                              </div>
                            )}
                            
                            {/* Notify threshold */}
                            <div className="text-xs text-stone-500 flex items-center gap-1">
                              <Bell size={12} />
                              Notify when ≤ {workshop.notifyAt} slots remaining
                            </div>
                            
                            {/* Attendee preview */}
                            <div className="mt-3 pt-3 border-t border-stone-200">
                              <div className="text-xs text-stone-500 mb-1">Registered:</div>
                              <div className="flex flex-wrap gap-1">
                                {workshop.registrations.slice(0, 6).map((reg, idx) => (
                                  <span 
                                    key={idx}
                                    className="px-2 py-0.5 bg-stone-100 text-stone-700 text-xs rounded"
                                    title={`${reg.payer_name} - ${reg.payer_email}`}
                                  >
                                    {reg.payer_name?.split(' ')[0] || 'N/A'}
                                  </span>
                                ))}
                                {workshop.registrations.length > 6 && (
                                  <span className="px-2 py-0.5 bg-stone-200 text-stone-600 text-xs rounded">
                                    +{workshop.registrations.length - 6}
                                  </span>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* WAITLIST TAB */}
        {activeTab === 'waitlist' && (
          <Card className="border-stone-200">
            <CardHeader className="border-b border-stone-200">
              <CardTitle className="text-lg font-serif flex items-center gap-2">
                <Clock className="text-orange-600" size={20} />
                Waitlist ({waitlist.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {waitlist.length === 0 ? (
                <div className="p-8 text-center text-stone-500">
                  <Clock size={48} className="mx-auto mb-4 text-stone-300" />
                  <p>No one on the waitlist</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Position</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Workshop</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Date Added</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {waitlist.map((entry) => (
                        <tr key={entry.id} className="hover:bg-stone-50">
                          <td className="px-4 py-3 text-sm font-medium text-stone-900">#{entry.position}</td>
                          <td className="px-4 py-3 text-sm text-stone-700">{entry.workshop_name}</td>
                          <td className="px-4 py-3 text-sm font-medium text-stone-900">{entry.name}</td>
                          <td className="px-4 py-3 text-sm text-stone-600">{entry.email}</td>
                          <td className="px-4 py-3 text-sm text-stone-600">{formatDate(entry.created_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(entry.status)}`}>
                              {entry.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {entry.status === 'waiting' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleNotifyWaitlist(entry.id)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Send notification email"
                                >
                                  <Send size={16} />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveFromWaitlist(entry.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Remove from waitlist"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* REGISTRATIONS TAB */}
        {activeTab === 'registrations' && (
          <>
            {/* Filters & Actions */}
            <Card className="border-stone-200 mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                      <Input
                        placeholder="Search name, email, workshop..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-full md:w-80"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Filter size={18} className="text-stone-400" />
                      <select
                        aria-label="Registration type"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="px-3 py-2 border border-stone-300 rounded-md text-sm"
                      >
                        <option value="all">All Types</option>
                        <option value="gallery_application">Gallery entry fees</option>
                        <option value="class">Classes</option>
                        <option value="workshop">Workshops</option>
                        <option value="membership">Memberships</option>
                        <option value="donation">Donations</option>
                        <option value="open_session">Open Sessions</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchData}>
                      <RefreshCw size={16} className="mr-2" /> Refresh
                    </Button>
                    <Button variant="outline" onClick={exportToCSV}>
                      <Download size={16} className="mr-2" /> Export
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Registrations Table */}
            <Card className="border-stone-200">
              <CardHeader className="border-b border-stone-200">
                <CardTitle className="text-lg font-serif">
                  All Registrations ({filteredRegistrations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {galleryFeeError&&<p className="p-4 text-red-800 bg-red-50" role="alert">{galleryFeeError}</p>}
                {filterType==='gallery_application'&&<p className="p-4 text-stone-700">Verified gallery entry-fee payments. Application status is separate. Showing up to 500 recent gallery payments.</p>}
                {filteredRegistrations.length === 0 ? (
                  <div className="p-8 text-center text-stone-500">
                    <DollarSign size={48} className="mx-auto mb-4 text-stone-300" />
                    <p>No registrations found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase cursor-pointer hover:bg-stone-100" onClick={() => handleSort('created_at')}>
                            <div className="flex items-center gap-1">Date <SortIcon field="created_at" /></div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Workshop / Item</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase cursor-pointer hover:bg-stone-100" onClick={() => handleSort('payer_name')}>
                            <div className="flex items-center gap-1">Attendee <SortIcon field="payer_name" /></div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase cursor-pointer hover:bg-stone-100" onClick={() => handleSort('total_amount')}>
                            <div className="flex items-center gap-1">Amount <SortIcon field="total_amount" /></div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {filteredRegistrations.map((reg) => (
                          <React.Fragment key={reg.id}>
                            <tr className="hover:bg-stone-50">
                              <td className="px-4 py-3 text-sm text-stone-600">{formatDate(reg.created_at)}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {getTypeIcon(reg.registration_type)}
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeBadgeColor(reg.registration_type)}`}>
                                    {formatRegType(reg.registration_type)}
                                  </span>
                                  {reg.registration_type === 'membership' && (() => {
                                    const t = reg.membership_transaction_type || 'legacy_unknown';
                                    const map = {
                                      new_membership: { label: 'New Membership', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-300' },
                                      renewal: { label: 'Renewal', cls: 'bg-blue-100 text-blue-800 border border-blue-300' },
                                      legacy_unknown: { label: 'Legacy / Unknown', cls: 'bg-stone-100 text-stone-700 border border-stone-300' },
                                    };
                                    const cfg = map[t] || map.legacy_unknown;
                                    return (
                                      <span
                                        className={`px-2 py-1 rounded-full text-[10px] font-medium ${cfg.cls}`}
                                        data-testid={`membership-txn-type-${reg.id}`}
                                      >
                                        {cfg.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-stone-900 max-w-xs truncate">{getWorkshopName(reg)}</div>
                              </td>
                              <td className="px-4 py-3">
                                {reg.registration_type === 'class' ? (
                                  <div>
                                    <div className="text-sm font-medium text-stone-900">
                                      {reg.registration_data?.student_name || reg.payer_name || 'N/A'}
                                    </div>
                                    <div className="text-xs text-stone-500">
                                      Parent: {reg.registration_data?.parent_name || reg.payer_name || 'N/A'}
                                    </div>
                                    <div className="text-xs text-stone-400">{reg.registration_data?.email || reg.payer_email || ''}</div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="text-sm font-medium text-stone-900">{reg.payer_name || 'N/A'}</div>
                                    <div className="text-xs text-stone-500">{reg.payer_email || 'N/A'}</div>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-emerald-600">{formatCurrency(reg.total_amount)}</td>
                              <td className="px-4 py-3">
                                {reg.registration_type==='gallery_application'?<GalleryRegistrationStatus registration={reg}/>:<span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(reg.status)}`}>{reg.status}</span>}
                              </td>
                              <td className="px-4 py-3">
                                <Button variant="ghost" size="sm" onClick={() => setExpandedRow(expandedRow === reg.id ? null : reg.id)}>
                                  <Eye size={16} className="mr-1" /> {expandedRow === reg.id ? 'Hide' : 'View'}
                                </Button>
                              </td>
                            </tr>
                            {expandedRow === reg.id && (
                              <tr className="bg-stone-50">
                                <td colSpan={7} className="px-4 py-4">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                      <h4 className="font-semibold text-stone-700 mb-2">Payment Details</h4>
                                      <p><span className="text-stone-500">Payment ID:</span> <span className="font-mono text-xs">{reg.payment_id}</span></p>
                                      <p><span className="text-stone-500">Registration ID:</span> <span className="font-mono text-xs">{reg.id}</span></p>
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-stone-700 mb-2">Items</h4>
                                      {reg.items?.map((item, idx) => (
                                        <p key={idx}>{item.name} - {formatCurrency(item.price)} x {item.quantity}</p>
                                      ))}
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-stone-700 mb-2">Contact</h4>
                                      {reg.registration_type === 'class' ? (
                                        <>
                                          <p><span className="text-stone-500">Student:</span> {reg.registration_data?.student_name || 'N/A'}</p>
                                          {reg.registration_data?.student_age && (
                                            <p><span className="text-stone-500">Age:</span> {reg.registration_data.student_age}</p>
                                          )}
                                          {reg.registration_data?.student_grade && (
                                            <p><span className="text-stone-500">Grade:</span> {reg.registration_data.student_grade}</p>
                                          )}
                                          <p className="mt-1"><span className="text-stone-500">Parent:</span> {reg.registration_data?.parent_name || reg.payer_name || 'N/A'}</p>
                                          <p><span className="text-stone-500">Email:</span> {reg.registration_data?.email || reg.payer_email || 'N/A'}</p>
                                          <p><span className="text-stone-500">Phone:</span> {reg.registration_data?.phone || 'N/A'}</p>
                                          {reg.registration_data?.emergency_contact?.name && (
                                            <p className="mt-1"><span className="text-stone-500">Emergency:</span> {reg.registration_data.emergency_contact.name} ({reg.registration_data.emergency_contact.phone})</p>
                                          )}
                                          {reg.status === 'completed' && !reg.enrollment_status && (
                                            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs">
                                              Payment captured but enrollment may need verification.
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <p><span className="text-stone-500">Name:</span> {reg.payer_name || 'N/A'}</p>
                                          <p><span className="text-stone-500">Email:</span> {reg.payer_email || 'N/A'}</p>
                                        </>
                                      )}
                                      {reg.registration_type==='gallery_application'&&<GalleryRegistrationDetails registration={reg}/>}
                                      {reg.status === 'completed' && reg.registration_type!=='gallery_application' && (
                                        <div className="mt-3 pt-3 border-t border-stone-200">
                                          <div className="flex items-center gap-2 mb-2">
                                            {reg.confirmation_email_sent ? (
                                              <span className="flex items-center text-xs text-emerald-600">
                                                <CheckCircle size={12} className="mr-1" /> Confirmation sent
                                              </span>
                                            ) : (
                                              <span className="flex items-center text-xs text-amber-600">
                                                <AlertCircle size={12} className="mr-1" /> Confirmation not sent
                                              </span>
                                            )}
                                          </div>
                                          <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => handleResendConfirmation(reg.id)}
                                            className="text-xs"
                                          >
                                            <Send size={12} className="mr-1" />
                                            {reg.confirmation_email_sent ? 'Resend' : 'Send'} Confirmation
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRegistrations;
